'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

import { useCreditsBalance } from '@/hooks/use-credits';
import useSWR from '@/hooks/use-swr';
import type { NotificationItem } from '@/lib/notifications/types';
import { useOrg } from '@/lib/org-provider';
import { notifications as previewNotifications } from '@/lib/shell/mock-data';

export interface ShellNotification {
  id: string;
  title: string;
  message: string;
  timeLabel: string;
  actionUrl?: string;
}

interface ShellRuntimeValue {
  providerMounted: boolean;
  isLoading: boolean;
  orgName: string | null;
  orgInitial: string;
  userEmail: string | null;
  creditsBalance: number | null;
  creditsLabel: string | null;
  notifications: ShellNotification[];
  notificationsLoading: boolean;
  notificationsError: string | null;
}

type NotificationsResponsePayload = {
  success?: boolean;
  data?: {
    notifications?: NotificationItem[];
  };
};

const PREVIEW_NOTIFICATIONS: ShellNotification[] = previewNotifications.map((notification) => ({
  id: notification.id,
  title: notification.fromHunter ? 'Hunter' : 'Notification',
  message: notification.text,
  timeLabel: notification.time,
}));

const PREVIEW_RUNTIME: ShellRuntimeValue = {
  providerMounted: false,
  isLoading: false,
  orgName: 'Glow',
  orgInitial: 'G',
  userEmail: 'attilio@glow.com',
  creditsBalance: 124,
  creditsLabel: '124cr',
  notifications: PREVIEW_NOTIFICATIONS,
  notificationsLoading: false,
  notificationsError: null,
};

const ShellRuntimeContext = createContext<ShellRuntimeValue | null>(null);

function getOrgInitial(orgName: string | null | undefined) {
  if (!orgName) return 'W';

  const match = orgName.trim().match(/[A-Za-z0-9]/);
  return match?.[0]?.toUpperCase() ?? 'W';
}

function formatCreditsLabel(balance: number | null) {
  if (typeof balance !== 'number' || Number.isNaN(balance)) {
    return null;
  }

  return `${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(balance)}cr`;
}

function formatRelativeTimeLabel(createdAt: string) {
  const createdMs = new Date(createdAt).getTime();
  if (!Number.isFinite(createdMs)) return 'Now';

  const diffMs = Date.now() - createdMs;
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs < minuteMs) return 'Now';
  if (diffMs < hourMs) return `${Math.max(1, Math.round(diffMs / minuteMs))}m ago`;
  if (diffMs < dayMs) return `${Math.max(1, Math.round(diffMs / hourMs))}h ago`;
  return `${Math.max(1, Math.round(diffMs / dayMs))}d ago`;
}

async function fetchShellNotifications(): Promise<ShellNotification[]> {
  const response = await fetch('/api/notifications?limit=8', {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error('Unable to load notifications');
  }

  const payload = (await response.json()) as NotificationsResponsePayload;
  const items = Array.isArray(payload?.data?.notifications)
    ? payload.data?.notifications ?? []
    : [];

  return items.map((item) => ({
    id: item.id,
    title: item.title,
    message: item.message,
    timeLabel: formatRelativeTimeLabel(item.createdAt),
    actionUrl: item.actionUrl,
  }));
}

export function ShellRuntimeProvider({ children }: { children: ReactNode }) {
  const { org, user, loading: orgLoading } = useOrg();
  const creditsQuery = useCreditsBalance();

  const notificationsFetcher = useCallback(() => fetchShellNotifications(), []);
  const notificationsQuery = useSWR<ShellNotification[]>(
    org?.id ? ['shell-notifications', org.id] : null,
    notificationsFetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 60_000,
    }
  );

  const creditsBalance =
    creditsQuery.data?.balance ??
    (typeof org?.credits_balance === 'number' ? org.credits_balance : null);

  const value = useMemo<ShellRuntimeValue>(
    () => ({
      providerMounted: true,
      isLoading: orgLoading,
      orgName: org?.name ?? null,
      orgInitial: getOrgInitial(org?.name),
      userEmail: user?.email ?? null,
      creditsBalance,
      creditsLabel: formatCreditsLabel(creditsBalance),
      notifications: notificationsQuery.data ?? [],
      notificationsLoading: orgLoading || notificationsQuery.isLoading,
      notificationsError:
        notificationsQuery.error instanceof Error
          ? notificationsQuery.error.message
          : notificationsQuery.error
            ? 'Unable to load notifications'
            : null,
    }),
    [
      creditsBalance,
      notificationsQuery.data,
      notificationsQuery.error,
      notificationsQuery.isLoading,
      org?.name,
      orgLoading,
      user?.email,
    ]
  );

  return (
    <ShellRuntimeContext.Provider value={value}>
      {children}
    </ShellRuntimeContext.Provider>
  );
}

export function useShellRuntime() {
  const value = useContext(ShellRuntimeContext);
  return value ?? PREVIEW_RUNTIME;
}
